import React, { useEffect, useState, useCallback } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import Button from "../../../../components/new-button";
import useCommonStore from "../../../../stores/common";
import useShippingStore from "../../../../stores/shipping";
import TextInput from "../../../../components/form/text-input";
import Select from "../../../../components/form/select";
import { loadShippingOfficeOptions } from "../../../../services/shipping";
import DarkCross from "../../../../assets/svg/icons/common/dark-cross";
import CloseIcon24 from "../../../../assets/svg/icons/common/CloseIcon24";
import CalendarIcon from "../../../../assets/svg/icons/common/Calender";
import styles from "./styles.module.scss";

const INITIAL_CLEARED = {
  page: 1,
  search: "",
  office: "",
  type: "All",
  courier: "All",
  orderDate: { from: "", to: "" },
  shipDate: { from: "", to: "" },
  sortBy: "ship_date",
  sortValue: "DESC",
};

const TYPE_OPTIONS = [
  { value: "All", label: "All" },
  { value: "Sales", label: "Sales" },
  { value: "Service", label: "Service" },
];

const COURIER_OPTIONS = [
  { value: "All", label: "All" },
  { value: "AIR", label: "AIR" },
  { value: "UPS", label: "UPS" },
  { value: "DHL", label: "DHL" },
  { value: "USPS", label: "USPS" },
  { value: "OWN LOG.", label: "OWN LOG." },
  { value: "FEDEX", label: "FEDEX" },
];

function FilterDrawer() {
  const filterDrawerOpen = useCommonStore((state) => state.filterDrawerOpen);
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );
  const setFilters = useShippingStore(
    (state) => state.setShippingFilterSearchQuery,
  );
  const officeOptions = useShippingStore((state) => state.officeOptions);
  const officeOptionsLoaded = useShippingStore(
    (state) => state.officeOptionsLoaded,
  );
  const [tempFilters, setTempFilters] = useState(
    () => useShippingStore.getState().shippingFilterSearchQuery,
  );
  const isMobile = useMediaQuery(768);
  const isSmallMobile = useMediaQuery(420);

  useEffect(() => {
    if (!filterDrawerOpen) {
      return;
    }
    setTempFilters(useShippingStore.getState().shippingFilterSearchQuery);
    if (!officeOptionsLoaded) {
      loadShippingOfficeOptions();
    }
  }, [filterDrawerOpen, officeOptionsLoaded]);

  const handleClear = () => {
    setTempFilters(INITIAL_CLEARED);
    setFilters(INITIAL_CLEARED);
    useShippingStore.getState().setCurrentPage(1);
    setFilterDrawerOpen(false);
  };

  const handleApply = () => {
    setFilters({ ...tempFilters, page: 1 });
    useShippingStore.getState().setCurrentPage(1);
    setFilterDrawerOpen(false);
  };

  const handleRemoveFilter = (type, value) => {
    setTempFilters((prev) => {
      switch (type) {
        case "office":
          return {
            ...prev,
            office: "",
          };
        case "type":
          return { ...prev, type: "All" };
        case "courier":
          return { ...prev, courier: "All" };
        case "orderDate":
          return { ...prev, orderDate: { from: "", to: "" } };
        case "shipDate":
          return { ...prev, shipDate: { from: "", to: "" } };
        default:
          return prev;
      }
    });
  };

  const handleDateChange = useCallback((type, field, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  }, []);

  const hasActiveFilters =
    (tempFilters.office && tempFilters.office !== "") ||
    (tempFilters.type && tempFilters.type !== "All") ||
    (tempFilters.courier && tempFilters.courier !== "All") ||
    tempFilters.orderDate.from ||
    tempFilters.orderDate.to ||
    tempFilters.shipDate.from ||
    tempFilters.shipDate.to;

  return (
    <Drawer
      open={filterDrawerOpen}
      direction={isMobile ? "left" : "right"}
      className={styles.filter_drawer}
      size={isSmallMobile ? "320px" : isMobile ? "375px" : "440px"}
      zIndex={8888}
      overlayColor={"#000"}
      overlayOpacity={0.5}
      onClose={() => setFilterDrawerOpen(false)}
    >
      <div className={styles.drawer_container}>
        <div className={styles.head}>
          <ButtonIcon onClick={() => setFilterDrawerOpen(false)}>
            <CloseIcon24 />
          </ButtonIcon>
        </div>

        <div className={styles.header}>
          <h5>Filter</h5>
          {hasActiveFilters && (
            <div className={styles.active_filters}>
              {tempFilters.office && tempFilters.office !== "" && (
                <div key={tempFilters.office} className={styles.filter_chip}>
                  <span>Office number: {tempFilters.office}</span>
                  <button type="button" onClick={() => handleRemoveFilter("office")}>
                    <DarkCross />
                  </button>
                </div>
              )}
              {tempFilters.type && tempFilters.type !== "All" && (
                <div className={styles.filter_chip}>
                  <span>Type: {tempFilters.type}</span>
                  <button onClick={() => handleRemoveFilter("type")}>
                    <DarkCross />
                  </button>
                </div>
              )}
              {tempFilters.courier && tempFilters.courier !== "All" && (
                <div className={styles.filter_chip}>
                  <span>Courier: {tempFilters.courier}</span>
                  <button onClick={() => handleRemoveFilter("courier")}>
                    <DarkCross />
                  </button>
                </div>
              )}
              {(tempFilters.orderDate.from || tempFilters.orderDate.to) && (
                <div className={styles.filter_chip}>
                  <span>
                    {tempFilters.orderDate.from && tempFilters.orderDate.to
                      ? `${tempFilters.orderDate.from} - ${tempFilters.orderDate.to}`
                      : tempFilters.orderDate.from
                        ? `From ${tempFilters.orderDate.from}`
                        : `To ${tempFilters.orderDate.to}`}
                  </span>
                  <button onClick={() => handleRemoveFilter("orderDate")}>
                    <DarkCross />
                  </button>
                </div>
              )}
              {(tempFilters.shipDate.from || tempFilters.shipDate.to) && (
                <div className={styles.filter_chip}>
                  <span>
                    {tempFilters.shipDate.from && tempFilters.shipDate.to
                      ? `${tempFilters.shipDate.from} - ${tempFilters.shipDate.to}`
                      : tempFilters.shipDate.from
                        ? `From ${tempFilters.shipDate.from}`
                        : `To ${tempFilters.shipDate.to}`}
                  </span>
                  <button onClick={() => handleRemoveFilter("shipDate")}>
                    <DarkCross />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.content}>
          <div className={styles.filter_section}>
            <h6 className={styles.title}>OFFICE NUMBER</h6>
            <div className={styles.filter_body}>
              <Select
                id="office-select"
                placeholder="Select office"
                value={tempFilters.office || ""}
                options={officeOptions.map((o) => ({ value: o, label: o }))}
                onChange={(selected) => {
                  setTempFilters((prev) => ({
                    ...prev,
                    office: selected || "",
                  }));
                }}
              />
            </div>
          </div>

          <div className={styles.filter_section}>
            <h6 className={styles.title}>TYPE</h6>
            <div className={styles.filter_body}>
              <Select
                id="type-select"
                placeholder="All"
                value={tempFilters.type || "All"}
                options={TYPE_OPTIONS}
                onChange={(selected) => {
                  setTempFilters((prev) => ({
                    ...prev,
                    type: selected || "All",
                  }));
                }}
                hideClearButton
              />
            </div>
          </div>

          <div className={styles.filter_section}>
            <h6 className={styles.title}>COURIER</h6>
            <div className={styles.filter_body}>
              <Select
                id="courier-select"
                placeholder="All"
                value={tempFilters.courier || "All"}
                options={COURIER_OPTIONS}
                onChange={(selected) => {
                  setTempFilters((prev) => ({
                    ...prev,
                    courier: selected || "All",
                  }));
                }}
                hideClearButton
              />
            </div>
          </div>

          <div className={styles.filter_section}>
            <h6 className={styles.title}>ORDER DATE</h6>
            <div className={styles.period_label}>Date range</div>
            <div className={styles.filter_body}>
              <div className={styles.date_range_fields}>
                <TextInput
                  id="shipping-filter-order-date-from"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={tempFilters.orderDate.from}
                  onChange={(e) => handleDateChange("orderDate", "from", e.target.value)}
                  suffix={
                    <span className={styles.calendar_icon}>
                      <CalendarIcon />
                    </span>
                  }
                />
                <span className={styles.date_separator}>-</span>
                <TextInput
                  id="shipping-filter-order-date-to"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={tempFilters.orderDate.to}
                  onChange={(e) => handleDateChange("orderDate", "to", e.target.value)}
                  suffix={
                    <span className={styles.calendar_icon}>
                      <CalendarIcon />
                    </span>
                  }
                />
              </div>
            </div>
          </div>

          <div className={styles.filter_section}>
            <h6 className={styles.title}>SHIP DATE</h6>
            <div className={styles.period_label}>Date range</div>
            <div className={styles.filter_body}>
              <div className={styles.date_range_fields}>
                <TextInput
                  id="shipping-filter-ship-date-from"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={tempFilters.shipDate.from}
                  onChange={(e) => handleDateChange("shipDate", "from", e.target.value)}
                  suffix={
                    <span className={styles.calendar_icon}>
                      <CalendarIcon />
                    </span>
                  }
                />
                <span className={styles.date_separator}>-</span>
                <TextInput
                  id="shipping-filter-ship-date-to"
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={tempFilters.shipDate.to}
                  onChange={(e) => handleDateChange("shipDate", "to", e.target.value)}
                  suffix={
                    <span className={styles.calendar_icon}>
                      <CalendarIcon />
                    </span>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button variant="secondary" size="large" onClick={handleClear}>
            Clear
          </Button>
          <Button variant="primary" size="large" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

export default FilterDrawer;
